'use client';

/**
 * LinkToEntityModal Component
 * Modal for linking an ArchiMate element to a Command Center entity
 */

import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '@/lib/archimate/WorkspaceContext';
import type { Entity, EntityType } from '@/lib/command-center/types';
import { ENTITY_TYPE_LABELS, ENTITY_TO_ARCHIMATE_TYPES } from '@/lib/command-center/types';

interface ArchiMateElementForLinking {
  id: string;
  type: string;
  name: string;
  documentation?: string;
}

interface LinkToEntityModalProps {
  element: ArchiMateElementForLinking;
  onClose: () => void;
  onLinked: (entityId: string, mappingId: string) => void;
}

export default function LinkToEntityModal({
  element,
  onClose,
  onLinked,
}: LinkToEntityModalProps) {
  const { currentWorkspace } = useWorkspace();

  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [linking, setLinking] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);

  // Find compatible entity types for this ArchiMate element
  const compatibleTypes = Object.entries(ENTITY_TO_ARCHIMATE_TYPES)
    .filter(([_, archiTypes]) => archiTypes.includes(element.type))
    .map(([entityType]) => entityType as EntityType);

  // Load entities
  useEffect(() => {
    async function loadEntities() {
      if (!currentWorkspace?.id) return;

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          workspace_id: currentWorkspace.id,
          status: 'active',
          pageSize: '100',
        });

        const response = await fetch(`/api/secure/command-center/entities?${params}`);
        if (!response.ok) throw new Error('Failed to load entities');

        const data = await response.json();
        setEntities(data.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load entities');
      } finally {
        setLoading(false);
      }
    }

    loadEntities();
  }, [currentWorkspace?.id]);

  // Filter entities by search and compatibility
  const filteredEntities = entities.filter((entity) => {
    const matchesSearch = searchQuery === '' ||
      entity.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entity.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const isCompatible = compatibleTypes.length === 0 ||
      compatibleTypes.includes(entity.entity_type);

    return matchesSearch && isCompatible;
  });

  // Sort by name similarity to element name
  const sortedEntities = [...filteredEntities].sort((a, b) => {
    const aMatch = a.name.toLowerCase().includes(element.name.toLowerCase());
    const bMatch = b.name.toLowerCase().includes(element.name.toLowerCase());
    if (aMatch && !bMatch) return -1;
    if (!aMatch && bMatch) return 1;
    return a.name.localeCompare(b.name);
  });

  // Handle link creation
  const handleLink = async () => {
    if (!selectedEntity || !currentWorkspace?.id) return;

    setLinking(true);
    setError(null);

    try {
      const response = await fetch('/api/secure/command-center/archimate-mappings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_id: selectedEntity.id,
          archimate_workspace_id: currentWorkspace.id,
          archimate_element_id: element.id,
          mapping_type: 'exact',
          sync_direction: 'bidirectional',
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create mapping');
      }

      const mapping = await response.json();
      onLinked(selectedEntity.id, mapping.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create mapping');
    } finally {
      setLinking(false);
    }
  };

  // Handle create and link
  const handleCreateAndLink = async (name: string, entityType: EntityType, description: string) => {
    if (!currentWorkspace?.id) return;

    setLinking(true);
    setError(null);

    try {
      // Create entity
      const entityResponse = await fetch('/api/secure/command-center/entities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: currentWorkspace.id,
          entity_type: entityType,
          name,
          description,
        }),
      });

      if (!entityResponse.ok) {
        const data = await entityResponse.json();
        throw new Error(data.error || 'Failed to create entity');
      }

      const newEntity = await entityResponse.json();

      // Create mapping
      const mappingResponse = await fetch('/api/secure/command-center/archimate-mappings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_id: newEntity.id,
          archimate_workspace_id: currentWorkspace.id,
          archimate_element_id: element.id,
          mapping_type: 'exact',
          sync_direction: 'bidirectional',
        }),
      });

      if (!mappingResponse.ok) {
        const data = await mappingResponse.json();
        throw new Error(data.error || 'Failed to create mapping');
      }

      const mapping = await mappingResponse.json();
      onLinked(newEntity.id, mapping.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create and link');
    } finally {
      setLinking(false);
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70" onClick={onClose} />
        <div className="relative bg-surface border border-border rounded-xl w-full max-w-md p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">No Workspace Selected</h2>
          <p className="text-foreground-secondary mb-4">
            Please select or create an ArchiMate workspace to use Command Center features.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-surface border border-border rounded-xl w-full max-w-lg shadow-xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-muted">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Link to Command Center</h2>
            <p className="text-foreground-tertiary text-sm mt-1">
              Element: <span className="text-accent">{element.name}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-foreground-tertiary hover:text-foreground p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="bg-red-900/50 border border-red-500/50 rounded-lg p-3 mb-4">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {!showCreateNew ? (
            <>
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search entities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-border-muted0"
                />
              </div>

              {/* Compatible types hint */}
              {compatibleTypes.length > 0 && (
                <p className="text-foreground-tertiary text-xs mb-4">
                  Compatible types: {compatibleTypes.map(t => ENTITY_TYPE_LABELS[t]).join(', ')}
                </p>
              )}

              {/* Entity list */}
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent" />
                </div>
              ) : sortedEntities.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-foreground-tertiary mb-4">
                    {searchQuery
                      ? 'No matching entities found'
                      : 'No entities in this workspace yet'}
                  </p>
                  <button
                    onClick={() => setShowCreateNew(true)}
                    className="text-accent hover:underline"
                  >
                    Create new entity
                  </button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-auto">
                  {sortedEntities.map((entity) => (
                    <button
                      key={entity.id}
                      onClick={() => setSelectedEntity(entity)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedEntity?.id === entity.id
                          ? 'border-accent bg-accent-muted'
                          : 'border-border-muted hover:border-border/30 bg-background-dark/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-foreground font-medium">{entity.name}</div>
                          <div className="text-foreground-tertiary text-sm">
                            {ENTITY_TYPE_LABELS[entity.entity_type]}
                          </div>
                        </div>
                        {selectedEntity?.id === entity.id && (
                          <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      {entity.description && (
                        <p className="text-foreground-muted text-sm mt-1 line-clamp-1">
                          {entity.description}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Create new link */}
              {sortedEntities.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border-muted text-center">
                  <button
                    onClick={() => setShowCreateNew(true)}
                    className="text-accent hover:underline text-sm"
                  >
                    Or create a new entity
                  </button>
                </div>
              )}
            </>
          ) : (
            <CreateNewEntityForm
              elementName={element.name}
              elementDocumentation={element.documentation}
              compatibleTypes={compatibleTypes}
              onSubmit={handleCreateAndLink}
              onCancel={() => setShowCreateNew(false)}
              loading={linking}
            />
          )}
        </div>

        {/* Footer */}
        {!showCreateNew && (
          <div className="flex items-center justify-end gap-3 p-4 border-t border-border-muted">
            <button
              onClick={onClose}
              className="px-4 py-2 text-foreground-secondary hover:text-foreground"
              disabled={linking}
            >
              Cancel
            </button>
            <button
              onClick={handleLink}
              disabled={!selectedEntity || linking}
              className="px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {linking ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-navy" />
                  <span>Linking...</span>
                </>
              ) : (
                <span>Link Entity</span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Create New Entity Form
function CreateNewEntityForm({
  elementName,
  elementDocumentation,
  compatibleTypes,
  onSubmit,
  onCancel,
  loading,
}: {
  elementName: string;
  elementDocumentation?: string;
  compatibleTypes: EntityType[];
  onSubmit: (name: string, type: EntityType, description: string) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const [name, setName] = useState(elementName);
  const [entityType, setEntityType] = useState<EntityType>(
    compatibleTypes.length > 0 ? compatibleTypes[0] : 'technology'
  );
  const [description, setDescription] = useState(elementDocumentation || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit(name.trim(), entityType, description.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">
          Entity Type
        </label>
        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value as EntityType)}
          className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground focus:outline-none focus:border-border-muted0"
        >
          {compatibleTypes.length > 0 ? (
            compatibleTypes.map((type) => (
              <option key={type} value={type}>
                {ENTITY_TYPE_LABELS[type]}
              </option>
            ))
          ) : (
            Object.entries(ENTITY_TYPE_LABELS).map(([type, label]) => (
              <option key={type} value={type}>
                {label}
              </option>
            ))
          )}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">
          Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground focus:outline-none focus:border-border-muted0"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full bg-background-dark border border-border rounded-lg px-4 py-2 text-foreground focus:outline-none focus:border-border-muted0 resize-none"
          rows={3}
        />
      </div>

      <div className="flex items-center justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-foreground-secondary hover:text-foreground"
          disabled={loading}
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!name.trim() || loading}
          className="px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-navy" />
              <span>Creating...</span>
            </>
          ) : (
            <span>Create & Link</span>
          )}
        </button>
      </div>
    </form>
  );
}
